import React, { useContext, useMemo } from 'react';
import * as R from 'ramda';

import WorkUsers from '@atom/components/common/workUsers/WorkUsers';
import { Icon, ListTable, Menu } from '@atom/mui';
import { ContentCopyIcon, DeleteIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { UserDetail } from '@atom/types/user';
import { WorkOrder, WorkOrderUserGroup } from '@atom/types/work';
import {
  WorkOrderColumn,
  WorkOrderColumnProperty,
} from '@atom/types/workColumns';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { isManagerWorkDeleteRoleEnabled } from '@atom/utilities/tenantFeatureUtilities';
import {
  getLocalDisplayDate,
  setDisplayDate,
  setDurationDisplay,
} from '@atom/utilities/timeUtilities';
import {
  getUserFullName,
  rollUpTaskUsers,
} from '@atom/utilities/userUtilities';
import workOrderPriorityUtilities from '@atom/utilities/workOrderPriorityUtilities';
import {
  showWorkDateTime,
  showWorkDueDateTime,
} from '@atom/utilities/workPreferenceUtilities';

import WorkOrderStatusPill from '../common/workOrderDetail/workOrderStatusPill/WorkOrderStatusPill';
import WorkPriorityIcon from '../common/WorkPriorityIcon';
import WorkTasks from '../common/workTasks/WorkTasks';

import CustomTaskFieldCell from './CustomTaskFieldCell';
import CustomWorkFieldCell from './CustomWorkFieldCell';
import WorkOrdersContext from './WorkOrdersContext';

import classes from './workOrders.module.css';

const { TableRow, TableCell } = ListTable;

const { MenuItem } = Menu;

interface Props {
  workOrder: WorkOrder;
  loadingDuplicate: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
}

const styles = {
  link: {
    fontWeight: 500,
  },
};

const formatWorkDate = (date: number | undefined, withTime: boolean): string =>
  withTime
    ? getLocalDisplayDate(date, 'MM/dd/yyyy hh:mm a', false) || '-'
    : setDisplayDate(date);

const WorkOrderTableRow = ({
  workOrder,
  onDuplicate,
  loadingDuplicate,
  onDelete,
}: Props) => {
  const { input, setInput, workOrderColumns } = useContext(WorkOrdersContext);

  const isDeleteEnabled = isManagerWorkDeleteRoleEnabled
    ? hasRolePermissions(ROLE_SETS.MANAGER)
    : hasRolePermissions(ROLE_SETS.ORG_ADMIN);
  const isDuplicateEnabled = hasRolePermissions(ROLE_SETS.INSPECTOR);
  const showActionMenu = isDeleteEnabled || isDuplicateEnabled;

  const userGroups = useMemo(() => {
    return workOrder.tasks.reduce(
      (acc: WorkOrderUserGroup[], task) => [...acc, ...task.userGroups],
      [],
    );
  }, [workOrder]);

  const rowBackgroundColor =
    input.workOrderId === workOrder.id
      ? colors.neutral.typhoon
      : colors.neutral.white;

  const getTableCell = (column: WorkOrderColumn) => {
    const columnTask = column.taskId
      ? workOrder.tasks.find(task => task.id === column.taskId)
      : undefined;

    const cellContent = {
      [WorkOrderColumnProperty.WORK_FIELD]: (
        <CustomWorkFieldCell workOrder={workOrder} column={column} />
      ),
      [WorkOrderColumnProperty.TASK_FIELD]: (
        <CustomTaskFieldCell workOrder={workOrder} column={column} />
      ),
      [WorkOrderColumnProperty.PRIORITY]: (
        <>
          <WorkPriorityIcon priorityId={workOrder.priorityId} />
          <span>
            {workOrderPriorityUtilities.getWorKOrderListPriorityLabel(
              workOrder?.priorityId,
            )}
          </span>
        </>
      ),
      [WorkOrderColumnProperty.STATUS]: (
        <WorkOrderStatusPill
          className="table"
          statusId={workOrder?.statusId}
          style={{ margin: 0 }}
        />
      ),
      [WorkOrderColumnProperty.INVENTORY_NAME]: workOrder?.inventoryAssetName,
      [WorkOrderColumnProperty.DUE_DATE]: (
        <div className={classes.noWrapCell}>
          {formatWorkDate(
            column.taskId ? columnTask?.dueDate : workOrder?.dueDate,
            showWorkDueDateTime(workOrder),
          )}
        </div>
      ),
      [WorkOrderColumnProperty.PLANNED_START_DATE]: (
        <div className={classes.noWrapCell}>
          {getLocalDisplayDate(
            workOrder?.plannedStartDate,
            'MM/dd/yyyy hh:mm a',
            false,
          ) || '-'}
        </div>
      ),
      [WorkOrderColumnProperty.ASSIGNED_TO]: (
        <WorkUsers
          users={rollUpTaskUsers(workOrder?.tasks as any)}
          userGroups={userGroups}
        />
      ),
      [WorkOrderColumnProperty.COMPLETED_DATE]: (
        <div className={classes.noWrapCell}>
          {formatWorkDate(
            column.taskId
              ? columnTask?.completedDate
              : workOrder?.completionDate,
            showWorkDateTime(workOrder),
          )}
        </div>
      ),
      [WorkOrderColumnProperty.COMPLETED_BY]: workOrder.completedBy
        ? getUserFullName(workOrder?.completedBy as UserDetail)
        : '-',
      [WorkOrderColumnProperty.NUM_TASKS]: (
        <WorkTasks
          tasks={workOrder?.tasks}
          showDateTime={showWorkDateTime(workOrder)}
        />
      ),
      [WorkOrderColumnProperty.NUM_FORMS]: workOrder.tasks.reduce(
        (acc, task) => {
          return acc + R.length(task.formInstanceIds);
        },
        0,
      ),
      [WorkOrderColumnProperty.WORK_TYPE]: workOrder?.workTemplate?.name,
      [WorkOrderColumnProperty.CREATED_DATE]: setDisplayDate(
        workOrder?.createdDate,
      ),
      [WorkOrderColumnProperty.CREATED_BY]: workOrder.createdBy
        ? getUserFullName(workOrder?.createdBy as UserDetail)
        : '-',
      [WorkOrderColumnProperty.INVENTORY_UPDATE_DATE]: setDisplayDate(
        workOrder?.appliedDate,
      ),
      [WorkOrderColumnProperty.START_DATE]: setDisplayDate(
        column.taskId
          ? workOrder.tasks.find(task => task.id === column.taskId)?.startTime
          : workOrder?.startTime,
      ),
      [WorkOrderColumnProperty.DURATION]: setDurationDisplay(
        column.taskId
          ? workOrder.tasks.find(task => task.id === column.taskId)?.duration
          : workOrder?.duration,
      ),
    };

    const content = cellContent[column?.property] || null;

    return <TableCell key={column?.label}>{content}</TableCell>;
  };

  return (
    <TableRow key={workOrder.id} backgroundColor={rowBackgroundColor}>
      <TableCell style={styles.link}>
        <div
          className={classes.workOrderName}
          onClick={() => setInput({ ...input, workOrderId: workOrder.id })}
        >
          {workOrder.name}
        </div>
      </TableCell>
      {workOrderColumns.map(column => getTableCell(column))}
      <TableCell>
        {showActionMenu && (
          <Menu dataCyLabel={`workOrderListOptions-${workOrder.name}`}>
            {isDuplicateEnabled && (
              <MenuItem
                startAdornment={<Icon icon={ContentCopyIcon} />}
                onClick={onDuplicate}
                disabled={loadingDuplicate}
              >
                Duplicate
              </MenuItem>
            )}
            {isDeleteEnabled && (
              <MenuItem
                startAdornment={<Icon icon={DeleteIcon} />}
                onClick={onDelete}
              >
                Delete
              </MenuItem>
            )}
          </Menu>
        )}
      </TableCell>
    </TableRow>
  );
};

export default React.memo(WorkOrderTableRow);
